/**
 * Ride accessorial builder
 */

import { AccessorialType } from '../../constants/accessorial-types.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildRideOptions } from './types.js'
import { calcDelayPrice, calcDelayPay } from '../shared/calculations.js'
import { buildAccessorial } from '../shared/buildAccessorial.js'

/**
 * Build Ride Accessorial
 *
 * Used when a driver needs a rideshare (Uber/Lyft) to get to/from a vehicle.
 *
 * Cost Responsibility Breakdown:
 * - HopDrive: No direct cost (already accounted for in move cost)
 * - Customer: Pays ride cost + delay time if responsible
 * - Driver: Paid for idle time during ride
 *
 * @param options - Ride accessorial options
 * @returns Insertable ride accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const rideAccessorial = buildRideAccessorial({
 *   moveId: 12345,
 *   customerResponsible: true,
 *   rideCost: 24.50,
 *   driverIdleTimeMinutes: 30,
 *   customerPricePerMinute: 0.33,
 *   driverPayPerMinute: 0.17
 * })
 * // Result: { cost: 0, ar_amount: 34.40, ap_amount: 5.10, ... }
 * ```
 */
export function buildRideAccessorial(options: BuildRideOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build ride accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build ride accessorial: Invalid moveId prop!')
  }
  if (typeof options.customerResponsible !== 'boolean') {
    throw new Error('Failed to build ride accessorial: Invalid customerResponsible prop!')
  }
  if (typeof options.rideCost !== 'number') {
    throw new Error('Failed to build ride accessorial: Invalid rideCost prop!')
  }

  // Validation for optional props
  if (options.driverIdleTimeMinutes !== undefined && typeof options.driverIdleTimeMinutes !== 'number') {
    throw new Error('Failed to build ride accessorial: Invalid driverIdleTimeMinutes prop!')
  }
  if (options.customerPricePerMinute !== undefined && typeof options.customerPricePerMinute !== 'number') {
    throw new Error('Failed to build ride accessorial: Invalid customerPricePerMinute prop!')
  }
  if (options.driverPayPerMinute !== undefined && typeof options.driverPayPerMinute !== 'number') {
    throw new Error('Failed to build ride accessorial: Invalid driverPayPerMinute prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build ride accessorial: Invalid notes prop!')
  }

  // Calculate ride & delay price to customer (if customer is responsible)
  const ridePrice = options.customerResponsible ? options.rideCost : 0
  const delayPrice = options.customerResponsible
    ? calcDelayPrice(options.driverIdleTimeMinutes || 0, options.customerPricePerMinute)
    : 0

  // Calculate delay pay to driver
  const delayPay = calcDelayPay(options.driverIdleTimeMinutes || 0, options.driverPayPerMinute)

  // Build accessorial using base builder
  return buildAccessorial({
    moveId: options.moveId,
    code: AccessorialType.RIDE.code,
    cost: 0,
    ar_amount: ridePrice + delayPrice,
    ap_amount: delayPay,
    notes: options.notes || AccessorialType.RIDE.desc,
  })
}
